import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  useEconomicsSummary, useEconomicsTimeseries,
  useEconomicsBySku, useEconomicsCohorts,
  useEconomicsCompositeFunnel, useEconomicsByReadingType,
  type Period,
} from "@/api/admin/economics";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { KPICard, type KPIAccent } from "@/components/admin/KPICard";
import { PeriodPicker } from "@/components/admin/PeriodPicker";
import { RevenueCostChart } from "@/components/admin/RevenueCostChart";
import { SKUBreakdown } from "@/components/admin/SKUBreakdown";
import { ReadingTypeCosts } from "@/components/admin/ReadingTypeCosts";
import { CompositeFunnel } from "@/components/admin/CompositeFunnel";
import { CohortTable } from "@/components/admin/CohortTable";
import {
  formatCurrencyUSD, formatNumber, formatPercent, formatSignedCurrencyUSD,
} from "@/lib/format";
import { i18n } from "@/i18n";
import { getPlatform } from "@/lib/platform";
import { ADMIN_ECONOMICS_AUTOREFRESH_KEY } from "@/lib/storageKeys";

const REFETCH_KEY = ADMIN_ECONOMICS_AUTOREFRESH_KEY;

const pageStyle: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex", flexDirection: "column", gap: 16,
};
const headerRowStyle: CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
};
const titleStyle: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 22, color: "var(--d-fg)",
};
const subStyle: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};
const grid4: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};
const splitStyle: CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
};
const checkboxStyle: CSSProperties = {
  display: "flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};

export function Economics(): JSX.Element {
  const { t } = useTranslation("admin");
  const [params] = useSearchParams();
  const period = (params.get("period") as Period) ?? "last_30d";
  const lng = i18n.language || "en";

  // DUE-226 (F5): device-local storage is async on iOS (Capacitor
  // Preferences), so the toggle can't read it synchronously on first
  // render — start from the same "on by default" the old code assumed,
  // then hydrate from the real value once the platform storage resolves.
  const [auto, setAuto] = useState<boolean>(true);
  useEffect(() => {
    let cancelled = false;
    getPlatform()
      .storage.getItem(REFETCH_KEY)
      .then((value) => {
        if (!cancelled && value !== null) setAuto(value === "1");
      });
    return () => {
      cancelled = true;
    };
  }, []);
  function toggleAuto() {
    setAuto((prev) => {
      const next = !prev;
      void getPlatform().storage.setItem(REFETCH_KEY, next ? "1" : "0");
      return next;
    });
  }
  const refetchInterval: number | false = auto ? 60_000 : false;

  const summaryQuery = useEconomicsSummary(period, refetchInterval);
  const tsQuery = useEconomicsTimeseries(period, "day", refetchInterval);
  const skusQuery = useEconomicsBySku(period, refetchInterval);
  const byTypeQuery = useEconomicsByReadingType(period, refetchInterval);
  const cohortsQuery = useEconomicsCohorts(8, refetchInterval);
  const funnelQuery = useEconomicsCompositeFunnel(period, refetchInterval);

  const summary = summaryQuery.data;
  const ts = tsQuery.data ?? [];
  const skus = skusQuery.data ?? [];
  const byType = byTypeQuery.data ?? [];
  const cohorts = cohortsQuery.data ?? [];
  const funnel = funnelQuery.data;

  const netAccent: KPIAccent =
    summary && summary.contribution_usd > 0 ? "positive"
    : summary && summary.contribution_usd < 0 ? "negative"
    : "neutral";

  return (
    <div style={pageStyle}>
      <div style={headerRowStyle}>
        <div>
          <div style={titleStyle}>{t("economics.title")}</div>
          <div style={subStyle}>{t("economics.subtitle")}</div>
        </div>
        <label style={checkboxStyle}>
          <input type="checkbox" checked={auto} onChange={toggleAuto} />
          {t("economics.auto_refresh")}
        </label>
      </div>

      <PeriodPicker />

      {summaryQuery.isError ? (
        <AdminErrorState onRetry={() => void summaryQuery.refetch()} />
      ) : summary && (
        <>
          <div style={grid4}>
            <KPICard label={t("economics.kpi.revenue")}
                     value={formatCurrencyUSD(summary.revenue_usd, lng)} />
            <KPICard label={t("economics.kpi.llm_cost")}
                     value={formatCurrencyUSD(summary.llm_cost_usd, lng)} />
            <div data-testid="kpi-net">
              <KPICard label={t("economics.kpi.net")}
                       value={formatSignedCurrencyUSD(summary.contribution_usd, lng)}
                       accent={netAccent} />
            </div>
            <KPICard label={t("economics.kpi.margin")}
                     value={formatPercent(summary.contribution_margin * 100, lng, 0)} />
          </div>
          <div style={grid4}>
            <KPICard label={t("economics.kpi.free_users")}
                     value={formatNumber(summary.free_users, lng)} />
            <KPICard label={t("economics.kpi.paying_users")}
                     value={formatNumber(summary.paying_users, lng)} />
            <KPICard label={t("economics.kpi.conversion")}
                     value={formatPercent(summary.conversion_rate * 100, lng, 2)} />
            <KPICard label={t("economics.kpi.break_even")}
                     value={summary.break_even_conversion === null
                            ? "—"
                            : formatPercent(summary.break_even_conversion * 100, lng, 2)} />
          </div>
          <div style={grid4}>
            <KPICard label={t("economics.kpi.arpu_paying")}
                     value={formatCurrencyUSD(summary.arpu_paying, lng)} />
            <KPICard label={t("economics.kpi.cost_per_free")}
                     value={formatCurrencyUSD(summary.cost_per_free, lng)} />
          </div>
        </>
      )}

      {tsQuery.isError ? (
        <AdminErrorState onRetry={() => void tsQuery.refetch()} />
      ) : (
        <RevenueCostChart data={ts} />
      )}

      <div style={splitStyle}>
        {skusQuery.isError ? (
          <AdminErrorState onRetry={() => void skusQuery.refetch()} />
        ) : (
          <SKUBreakdown rows={skus} locale={lng} />
        )}
        {byTypeQuery.isError ? (
          <AdminErrorState onRetry={() => void byTypeQuery.refetch()} />
        ) : (
          <ReadingTypeCosts rows={byType} locale={lng} />
        )}
        {funnelQuery.isError ? (
          <AdminErrorState onRetry={() => void funnelQuery.refetch()} />
        ) : funnel ? (
          <CompositeFunnel data={funnel} locale={lng} />
        ) : (
          <div />
        )}
      </div>

      {cohortsQuery.isError ? (
        <AdminErrorState onRetry={() => void cohortsQuery.refetch()} />
      ) : (
        <CohortTable rows={cohorts} locale={lng} />
      )}
    </div>
  );
}
